import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "@/App";
import { useTranslation } from "@/hooks/useTranslation";
import api from "@/lib/api";
import {
  Wrench, MapPin, Navigation, CheckCircle2, Clock, X,
  LogOut, RefreshCw, ArrowLeft, HardHat, AlertTriangle,
  Settings, Loader2, Send, ChevronDown, ChevronUp, Fuel, BookOpen
} from "lucide-react";
import TutorialLedger, { TutorialToggle } from "@/components/shared/TutorialLedger";
import DashboardSwitcher from "@/components/shared/DashboardSwitcher";
import LiveMap from "@/components/shared/LiveMap";

export default function ServiceDashboard() {
  const { session, setSession, hubData, refreshData, logout } = useAppContext();
  const { t } = useTranslation(session?.lang);
  const nav = useNavigate();
  const { sites, machines, requests, events } = hubData;

  const [toast, setToast] = useState(null);
  const [expandedSite, setExpandedSite] = useState(null);
  const [expandedReq, setExpandedReq] = useState(null);
  const [resolveNotes, setResolveNotes] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showMapPanel, setShowMapPanel] = useState(false);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  // Filter requests that are repair or maintenance
  const serviceRequests = useMemo(() =>
    requests.filter(r => r.request_type === "REPARATION" || r.request_type === "MAINTENANCE")
      .sort((a, b) => {
        if (a.status === "DONE" && b.status !== "DONE") return 1;
        if (a.status !== "DONE" && b.status === "DONE") return -1;
        return new Date(b.created_at) - new Date(a.created_at);
      })
  , [requests]);

  const openRequests = serviceRequests.filter(r => r.status !== "DONE");
  const doneRequests = serviceRequests.filter(r => r.status === "DONE");

  // Group by site
  const siteMap = useMemo(() => {
    const map = {};
    sites.forEach(s => { map[s.id] = { ...s, machines: [], requests: [] }; });
    machines.forEach(m => { if (map[m.site_id]) map[m.site_id].machines.push(m); });
    serviceRequests.forEach(r => { if (map[r.site_id]) map[r.site_id].requests.push(r); });
    return map;
  }, [sites, machines, serviceRequests]);

  const activeSites = Object.values(siteMap).filter(s => s.requests.length > 0);

  const handleResolve = async (reqId) => {
    await api.approveRequest(reqId, "SERVICE");
    await api.createEvent({
      hub_id: session.hubId, event_type: "SERVICE_RESOLVED",
      message: `Service resolved request ${reqId}${resolveNotes ? ": " + resolveNotes : ""}`,
    });
    showToast("Request resolved!");
    setExpandedReq(null);
    setResolveNotes("");
    refreshData();
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refreshData();
    setIsRefreshing(false);
  };

  const getStatusBadge = (status) => {
    if (status === "DEFECT") return "bg-red-500/10 text-red-400 border-red-500/30";
    if (status === "UPKEEP") return "bg-purple-500/10 text-purple-400 border-purple-500/30";
    return "bg-slate-100 text-slate-400 border-slate-200";
  };

  return (
    <div className="min-h-[100dvh] bg-white flex flex-col" data-testid="service-dashboard">
      {/* Toast */}
      {toast && (
        <div className="fixed top-5 left-4 right-4 z-[1000] bg-purple-600 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-2 font-bold text-sm uppercase tracking-wider animate-slideInDown max-w-lg mx-auto">
          <CheckCircle2 className="w-4 h-4" /> {toast}
        </div>
      )}

      <TutorialLedger role="service" isOpen={showTutorial} onClose={() => setShowTutorial(false)} />

      {/* Header */}
      <header className="bg-white px-4 md:px-8 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center text-white shadow-[0_0_15px_rgba(147,51,234,0.3)]">
            <Wrench className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div>
            <h1 className="text-lg font-black tracking-tighter uppercase text-slate-900">Service Portal</h1>
            <p className="text-[10px] font-bold tracking-widest uppercase text-slate-400">{session?.hubName} / {session?.userName}</p>
          </div>
          <DashboardSwitcher currentRole="service" />
        </div>
        <div className="flex items-center gap-2">
          <TutorialToggle onClick={() => setShowTutorial(!showTutorial)} />
          <button data-testid="service-map-btn" onClick={() => setShowMapPanel(!showMapPanel)}
            className={`p-2.5 rounded-xl transition-all border ${showMapPanel ? "bg-blue-600 border-blue-500 text-white" : "bg-slate-100 border-slate-200 text-slate-400 hover:text-slate-900"}`}>
            <MapPin className="w-4 h-4" />
          </button>
          <button onClick={handleRefresh} data-testid="service-refresh"
            className={`p-2.5 bg-slate-100 border border-slate-200 rounded-xl text-slate-400 hover:text-slate-900 transition-all ${isRefreshing ? "animate-spin" : ""}`}>
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={() => { setSession(null); nav("/portal"); }} className="p-2.5 bg-slate-100 border border-slate-200 rounded-xl text-slate-400 hover:text-slate-900" data-testid="back-to-lobby-btn">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <button onClick={() => { logout(); nav("/"); }} data-testid="service-exit"
            className="p-2.5 bg-red-50 border border-red-200 rounded-xl text-red-500 hover:bg-red-600 hover:text-slate-900 transition-all">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Local Map */}
      {showMapPanel && (
        <div className="px-4 md:px-8 py-4 bg-slate-50/80 border-b border-slate-200 animate-slideInDown" data-testid="service-map-section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5 text-purple-500" /> Sites & Machines Map
            </h3>
            <button onClick={() => setShowMapPanel(false)} className="text-slate-400 hover:text-slate-900"><X className="w-4 h-4" /></button>
          </div>
          <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm">
            <LiveMap sites={sites} machines={machines} requests={requests} />
          </div>
        </div>
      )}

      <main className="flex-1 p-4 md:p-8 max-w-5xl mx-auto w-full">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="p-4 bg-slate-50 border border-red-500/20 rounded-xl text-center">
            <span className="text-2xl font-black text-red-400 font-mono">{openRequests.filter(r => r.request_type === "REPARATION").length}</span>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-1">Repairs</p>
          </div>
          <div className="p-4 bg-slate-50 border border-purple-500/20 rounded-xl text-center">
            <span className="text-2xl font-black text-purple-400 font-mono">{openRequests.filter(r => r.request_type === "MAINTENANCE").length}</span>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-1">Maintenance</p>
          </div>
          <div className="p-4 bg-slate-50 border border-lime-500/20 rounded-xl text-center">
            <span className="text-2xl font-black text-lime-600 font-mono">{doneRequests.length}</span>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-1">Resolved</p>
          </div>
        </div>

        {/* Open Requests */}
        <div className="mb-6">
          <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5 text-red-500" /> Active Service Requests ({openRequests.length})
          </h2>
          {openRequests.length === 0 ? (
            <div className="p-10 border border-dashed border-slate-200 rounded-xl text-center">
              <Wrench className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <p className="text-xs font-bold text-slate-400 uppercase">No pending service requests</p>
            </div>
          ) : (
            <div className="space-y-3">
              {openRequests.map(req => {
                const site = sites.find(s => s.id === req.site_id);
                const machine = machines.find(m => m.id === req.machine_id);
                const isRepair = req.request_type === "REPARATION";
                const isExpanded = expandedReq === req.id;
                return (
                  <div key={req.id} className={`bg-slate-50 border rounded-xl overflow-hidden transition-all ${
                    isRepair ? "border-red-500/30" : "border-purple-500/30"
                  }`} data-testid={`service-req-${req.id}`}>
                    <button onClick={() => setExpandedReq(isExpanded ? null : req.id)}
                      className="w-full p-4 flex items-center justify-between text-left">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isRepair ? "bg-red-600 text-white" : "bg-purple-600 text-white"}`}>
                          {isRepair ? <Wrench className="w-5 h-5" /> : <Settings className="w-5 h-5" />}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-slate-900">{req.machine_number}</span>
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${isRepair ? "bg-red-500/10 text-red-400" : "bg-purple-500/10 text-purple-400"}`}>
                              {req.request_type}
                            </span>
                            {req.urgency === "URGENT" && <span className="text-[9px] font-bold bg-red-600/20 text-red-400 px-1.5 py-0.5 rounded animate-pulse">URGENT</span>}
                          </div>
                          <p className="text-[10px] text-slate-400 font-bold mt-0.5">{site?.name} / {site?.city}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-slate-400 font-mono">{new Date(req.created_at).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}</span>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="p-4 border-t border-slate-200 space-y-3 animate-fadeIn">
                        {/* Machine details */}
                        {machine && (
                          <div className="p-3 bg-white rounded-xl border border-slate-200">
                            <div className="flex items-center gap-2 mb-2">
                              <HardHat className="w-4 h-4 text-amber-500" />
                              <span className="text-xs font-bold text-slate-800">{machine.name}</span>
                              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${getStatusBadge(machine.status_type)}`}>{machine.status_type || "NORMAL"}</span>
                            </div>
                            {machine.brand && <p className="text-[10px] text-slate-400">Brand: {machine.brand}</p>}
                            {machine.gps_lat > 0 && (
                              <a href={`https://www.google.com/maps?q=${machine.gps_lat},${machine.gps_lon}`} target="_blank" rel="noopener noreferrer"
                                className="text-[10px] text-lime-600 font-mono flex items-center gap-1 mt-1 hover:underline">
                                <Navigation className="w-3 h-3" /> {machine.gps_lat.toFixed(4)}, {machine.gps_lon.toFixed(4)}
                              </a>
                            )}
                          </div>
                        )}

                        {/* Site GPS */}
                        {site?.gps_lat > 0 && (
                          <a href={`https://www.google.com/maps?q=${site.gps_lat},${site.gps_lon}`} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-2 p-2.5 bg-white border border-slate-200 rounded-xl hover:border-lime-300 transition-all">
                            <MapPin className="w-4 h-4 text-red-500" />
                            <span className="text-[10px] font-mono text-lime-600">{site.gps_lat.toFixed(4)}, {site.gps_lon.toFixed(4)}</span>
                            <span className="text-[9px] text-slate-400 ml-auto">Site location</span>
                          </a>
                        )}

                        {/* Notes */}
                        {req.notes && <p className="text-xs text-slate-400 italic p-2 bg-white rounded-xl border border-slate-200">{req.notes}</p>}

                        {/* Resolve */}
                        <textarea data-testid={`resolve-notes-${req.id}`} value={resolveNotes} onChange={e => setResolveNotes(e.target.value)}
                          placeholder="Resolution notes..."
                          className="w-full p-3 bg-white border border-slate-200 rounded-xl text-xs text-slate-900 h-16 resize-none outline-none focus:border-purple-500/40 placeholder:text-slate-400" />
                        <button data-testid={`resolve-btn-${req.id}`} onClick={() => handleResolve(req.id)}
                          className={`w-full py-3.5 rounded-xl text-sm font-black uppercase flex items-center justify-center gap-2 active:scale-95 transition-all ${
                            isRepair ? "bg-red-600 text-white" : "bg-purple-600 text-white"
                          }`}>
                          <CheckCircle2 className="w-4 h-4" /> Mark Resolved
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Sites with machines */}
        <div className="mb-6">
          <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5 text-lime-500" /> Sites & Machine Inventory ({sites.length})
          </h2>
          <div className="space-y-2">
            {sites.map(site => {
              const sMachines = machines.filter(m => m.site_id === site.id);
              const isOpen = expandedSite === site.id;
              const problemMachines = sMachines.filter(m => m.status_type === "DEFECT" || m.status_type === "UPKEEP");
              return (
                <div key={site.id} className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden" data-testid={`service-site-${site.id}`}>
                  <button onClick={() => setExpandedSite(isOpen ? null : site.id)}
                    className="w-full p-4 flex items-center justify-between text-left hover:bg-slate-100/30 transition-all">
                    <div className="flex items-center gap-3">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      <div>
                        <span className="text-sm font-bold text-slate-800">{site.name}</span>
                        <span className="text-[10px] text-slate-400 ml-2">{site.city}</span>
                      </div>
                      {site.gps_lat > 0 && <Navigation className="w-3 h-3 text-lime-500" />}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold bg-slate-100 text-slate-400 px-2 py-0.5 rounded">{sMachines.length} machines</span>
                      {problemMachines.length > 0 && (
                        <span className="text-[10px] font-bold bg-red-500/10 text-red-400 px-2 py-0.5 rounded">{problemMachines.length} issues</span>
                      )}
                      {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    </div>
                  </button>
                  {isOpen && (
                    <div className="p-4 border-t border-slate-200 space-y-2 animate-fadeIn">
                      {site.gps_lat > 0 && (
                        <a href={`https://www.google.com/maps?q=${site.gps_lat},${site.gps_lon}`} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-2 p-2 bg-white border border-slate-200 rounded-xl mb-2 hover:border-lime-300">
                          <Navigation className="w-3.5 h-3.5 text-lime-500" />
                          <span className="text-[10px] font-mono text-lime-600">{site.gps_lat.toFixed(5)}, {site.gps_lon.toFixed(5)}</span>
                        </a>
                      )}
                      {sMachines.map(m => (
                        <div key={m.id} className="flex items-center justify-between p-2.5 bg-white rounded-xl border border-slate-200">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-slate-800 font-mono">{m.machine_number}</span>
                            <span className="text-[10px] text-slate-400">{m.name}</span>
                          </div>
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${getStatusBadge(m.status_type)}`}>
                            {m.status_type || "OK"}
                          </span>
                        </div>
                      ))}
                      {sMachines.length === 0 && <p className="text-[10px] text-slate-400 text-center py-2">No machines on site</p>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Resolved */}
        {doneRequests.length > 0 && (
          <div>
            <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-lime-500" /> Resolved ({doneRequests.length})
            </h2>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {doneRequests.slice(0, 20).map(req => (
                <div key={req.id} className="p-3 bg-slate-50/80 border border-slate-200 rounded-xl flex items-center justify-between opacity-60">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-400">{req.machine_number}</span>
                    <span className="text-[9px] text-slate-400">{req.request_type}</span>
                    <span className="text-[9px] text-slate-400">{sites.find(s => s.id === req.site_id)?.name}</span>
                  </div>
                  <CheckCircle2 className="w-4 h-4 text-lime-500" />
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
